(ns lupapalvelu.ui.auth-admin.reports.view
  "Authority admin reports page. ClojureScript/re-frame replacement for
  the old Knockout organization reports view."
  (:require ["date-fns" :refer [endOfDay endOfMonth getYear startOfDay startOfMonth subMonths subYears]]
            [lupapalvelu.next.event :refer [<sub >evt]]
            [lupapalvelu.ui.common :refer [loc]]
            [lupapalvelu.ui.components :as ui]
            [lupapalvelu.ui.components.organization-report-form :refer [report-form]]
            [re-frame.core :as rf]))

;; -- Initial date helpers -----------------------------------------------------

(defn- today []
  (js/Date.))

(defn- date-start-ts
  "Epoch ms for the start of the given js/Date day."
  [d]
  (.valueOf (startOfDay d)))

(defn- date-end-ts
  "Epoch ms for the end of the given js/Date day."
  [d]
  (.valueOf (endOfDay d)))

(defn- initial-dates
  "Returns a map of report-key → {:start ts :end ts} using date-fns arithmetic,
  matching the defaults from the original OrganizationReportsModel Knockout model."
  []
  (let [now         (today)
        one-month   (subMonths now 1)
        six-months  (subMonths now 6)
        one-year    (subYears now 1)
        month-start (startOfMonth now)]
    {:applications    {:start (date-start-ts one-month)
                       :end   (date-end-ts now)}
     :parties         {:start (date-start-ts one-month)
                       :end   (date-end-ts now)}
     :verdicts        {:start (date-start-ts one-month)
                       :end   (date-end-ts now)}
     :store-billing   {:start (date-start-ts month-start)
                       :end   (date-end-ts now)}
     :store-downloads {:start (date-start-ts month-start)
                       :end   (date-end-ts now)}
     :invoices        {:start (date-start-ts month-start)
                       :end   (date-end-ts now)}
     :archival        {:start (date-start-ts one-year)
                       :end   (date-end-ts now)}
     :onkalo          {:start (date-start-ts one-year)
                       :end   (date-end-ts now)}
     :bulletin        {:start (date-start-ts six-months)
                       :end   (date-end-ts now)}}))

(defn- last-month-start-ts
  "Epoch ms for the start of the first day of the previous month."
  []
  (let [now (today)]
    (date-start-ts (startOfMonth (subMonths now 1)))))

(defn- last-month-end-ts
  "Epoch ms for the end of the last day of the previous month."
  []
  (let [now (today)]
    (date-end-ts (endOfMonth (subMonths now 1)))))

;; -- Re-frame events ----------------------------------------------------------

(rf/reg-event-fx
  ::init
  (fn [{db :db} [_ org-id]]
    {:db       (assoc db ::state {:org-id     org-id
                                  :dates      (initial-dates)
                                  :waste-year (getYear (today))})
     :dispatch [::fetch-export-files]}))

(rf/reg-event-fx
  ::fetch-export-files
  (fn [{db :db} _]
    {:action/query {:name    :organization-by-user
                    :params  {:organizationId (:org-id (::state db))}
                    :success [::store-export-files]}}))

(rf/reg-event-db
  ::store-export-files
  (fn [db [_ {:keys [organization]}]]
    (assoc-in db [::state :export-files] (:export-files organization))))

(rf/reg-event-db
  ::set-start-date
  (fn [db [_ report-key date]]
    (assoc-in db [::state :dates report-key :start] (date-start-ts date))))

(rf/reg-event-db
  ::set-end-date
  (fn [db [_ report-key date]]
    (assoc-in db [::state :dates report-key :end] (date-end-ts date))))

(rf/reg-event-db
  ::set-waste-year
  (fn [db [_ year]]
    (assoc-in db [::state :waste-year] year)))

;; -- Re-frame subs ------------------------------------------------------------

(rf/reg-sub
  ::view-data
  (fn [db _]
    (::state db)))

(rf/reg-sub
  ::org-id
  :<- [::view-data]
  :-> :org-id)

(rf/reg-sub
  ::export-files
  :<- [::view-data]
  :-> :export-files)

(rf/reg-sub
  ::start-ts
  :<- [::view-data]
  (fn [view-data [_ report-key]]
    (get-in view-data [:dates report-key :start])))

(rf/reg-sub
  ::end-ts
  :<- [::view-data]
  (fn [view-data [_ report-key]]
    (get-in view-data [:dates report-key :end])))

(rf/reg-sub
  ::waste-year
  :<- [::view-data]
  :-> :waste-year)

(rf/reg-sub
  ::bad-year?
  :<- [::waste-year]
  (fn [waste-year _]
    (not (re-matches #"\s*\d+\s*" (str waste-year)))))

;; -- View components ----------------------------------------------------------

(defn- export-files-section
  "Attachment export files table, shown only when files are present."
  []
  (let [files  (<sub [::export-files])
        org-id (<sub [::org-id])]
    (when (seq files)
      [:section
       [:h2 (loc :authorityAdmin.attachment-exports)]
       [:table
        [:tbody
         (for [{:keys [filename fileId size created]} files]
           [:tr {:key fileId}
            [:td [:a {:href (str "/api/raw/download-organization-attachments-export-file"
                                 "?organizationId=" org-id
                                 "&fileId=" fileId)}
                  filename]]
            [:td (js/util.sizeString size)]
            [:td (js/util.finnishDateAndTime created)]])]]])))

(defn- dated-report
  "A date-range report form."
  [report-key {:keys [action title-loc caption-loc button-loc start-date-loc end-date-loc]}]
  (let [start-ts (<sub [::start-ts report-key])
        end-ts   (<sub [::end-ts report-key])
        org-id   (<sub [::org-id])]
    [report-form
     {:action               action
      :org-id               org-id
      :title-loc            title-loc
      :caption-loc          caption-loc
      :button-loc           button-loc
      :start-date           start-ts
      :end-date             end-ts
      :start-date-loc       start-date-loc
      :end-date-loc         end-date-loc
      :start-ts             start-ts
      :end-ts               end-ts
      :on-start-date-change #(>evt [::set-start-date report-key %])
      :on-end-date-change   #(>evt [::set-end-date report-key %])}]))

(defn- simple-report
  "A report form without a date range."
  [action {:keys [title-loc caption-loc button-loc]}]
  (let [org-id (<sub [::org-id])]
    [report-form
     {:action      action
      :org-id      org-id
      :title-loc   title-loc
      :caption-loc caption-loc
      :button-loc  button-loc}]))

(defn- last-month-shortcut
  "A date-range form pre-filled with last month's start/end, no date pickers."
  [action button-loc]
  (let [org-id (<sub [::org-id])]
    [report-form
     {:action     action
      :org-id     org-id
      :button-loc button-loc
      :start-ts   (last-month-start-ts)
      :end-ts     (last-month-end-ts)}]))

(defn- waste-report-section []
  (let [org-id    (<sub [::org-id])
        year      (<sub [::waste-year])
        bad-year? (<sub [::bad-year?])]
    [:section
     [:h2 (loc :auth-admin.waste-report.title)]
     [:div (loc :auth-admin.waste-report.description)]
     [:form {:action "/api/raw/waste-report"
             :method :get}
      [:input {:type  :hidden
               :name  :organizationId
               :value org-id}]
      [:div.labeled-input.row
       [:div.labeled-input.cell
        [:label.lux {:for "waste-report-year"}
         (loc :auth-admin.waste-report.year)]
        [:input.lux {:type      :text
                     :id        "waste-report-year"
                     :name      :year
                     :value     year
                     :on-change #(>evt [::set-waste-year (.. % -target -value)])}]]
       [:div.labeled-input.cell
        [ui/icon-button
         {:class       :primary.outline
          :disabled?   bad-year?
          :icon        :lupicon-download
          :text-loc    :authorityAdmin.onkaloApplicationsReport.buttonText
          :button-type :submit}]]]]]))

(defn view []
  [:section.page.visible
   {:id "reports"}
   [:div.container.container--authority-admin
    [:section.authority-admin-reports
     [export-files-section]
     [:section
      [simple-report :open-applications-xlsx
       {:title-loc   :authorityAdmin.openApplicationsReport.title
        :caption-loc :authorityAdmin.openApplicationsReport.caption
        :button-loc  :authorityAdmin.openApplicationsReport.buttonText}]]
     [:section
      [dated-report :applications
       {:action         :applications-between-xlsx
        :title-loc      :authorityAdmin.applicationsReportTimeperiod.title
        :caption-loc    :authorityAdmin.applicationsReportTimeperiod.caption
        :button-loc     :authorityAdmin.applicationsReportTimeperiod.buttonText
        :start-date-loc :authorityAdmin.applicationsReportTimeperiod.startTs
        :end-date-loc   :authorityAdmin.applicationsReportTimeperiod.endTs}]]
     [:section
      [dated-report :parties
       {:action         :parties-between-xlsx
        :title-loc      :authorityAdmin.partiesReports.title
        :caption-loc    :authorityAdmin.partiesReports.caption
        :button-loc     :authorityAdmin.applicationsReportTimeperiod.buttonText
        :start-date-loc :authorityAdmin.applicationsReportTimeperiod.startTs
        :end-date-loc   :authorityAdmin.applicationsReportTimeperiod.endTs}]]
     [:section
      [dated-report :verdicts
       {:action         :post-verdict-xlsx
        :title-loc      :authorityAdmin.postVerdictReports.title
        :caption-loc    :authorityAdmin.postVerdictReports.caption
        :button-loc     :authorityAdmin.applicationsReportTimeperiod.buttonText
        :start-date-loc :authorityAdmin.postVerdictReports.timePeriod.startTs
        :end-date-loc   :authorityAdmin.postVerdictReports.timePeriod.endTs}]]
     [:section
      [dated-report :store-billing
       {:action         :store-billing-report
        :title-loc      :authorityAdmin.storeBillingReports.title
        :caption-loc    :authorityAdmin.storeBillingReports.caption
        :button-loc     :authorityAdmin.storeBillingReports.buttonText
        :start-date-loc :authorityAdmin.storeBillingReports.timePeriod.startTs
        :end-date-loc   :authorityAdmin.storeBillingReports.timePeriod.endTs}]
      [last-month-shortcut :store-billing-report
       :authorityAdmin.storeBillingReports.lastMonthButtonText]]
     [:section
      [dated-report :store-downloads
       {:action         :store-downloads-report
        :title-loc      :authorityAdmin.storeDownloadsReports.title
        :caption-loc    :authorityAdmin.storeDownloadsReports.caption
        :button-loc     :authorityAdmin.storeBillingReports.buttonText
        :start-date-loc :authorityAdmin.storeDownloadsReports.timePeriod.startTs
        :end-date-loc   :authorityAdmin.storeDownloadsReports.timePeriod.endTs}]
      [last-month-shortcut :store-downloads-report
       :authorityAdmin.storeBillingReports.lastMonthButtonText]]
     [:section
      [dated-report :invoices
       {:action         :invoices-between-xlsx
        :title-loc      :authorityAdmin.invoicesReport.title
        :caption-loc    :authorityAdmin.invoicesReport.caption
        :button-loc     :authorityAdmin.storeBillingReports.buttonText
        :start-date-loc :authorityAdmin.invoicesReport.timePeriod.startTs
        :end-date-loc   :authorityAdmin.invoicesReport.timePeriod.endTs}]
      [last-month-shortcut :invoices-between-xlsx
       :authorityAdmin.invoicesReport.lastMonthButtonText]]
     [:section
      [simple-report :authorities-report
       {:title-loc   :authorityAdmin.authoritiesReport.title
        :caption-loc :authorityAdmin.authoritiesReport.caption
        :button-loc  :authorityAdmin.authoritiesReport.buttonText}]]
     [:section
      [dated-report :archival
       {:action         :archival-report
        :title-loc      :archival-report.title
        :caption-loc    :archival-report.description
        :button-loc     :authorityAdmin.authoritiesReport.buttonText
        :start-date-loc :archival-report.start-date
        :end-date-loc   :archival-report.end-date}]]
     [:section
      [dated-report :onkalo
       {:action         :onkalo-applications-xlsx
        :title-loc      :authorityAdmin.onkaloApplicationsReport.title
        :caption-loc    :authorityAdmin.onkaloApplicationsReport.caption
        :button-loc     :authorityAdmin.onkaloApplicationsReport.buttonText
        :start-date-loc :authorityAdmin.onkaloApplicationsReport.timePeriod.startTs
        :end-date-loc   :authorityAdmin.onkaloApplicationsReport.timePeriod.endTs}]]
     [waste-report-section]
     [:section
      [dated-report :bulletin
       {:action         :bulletin-report
        :title-loc      :bulletin-report.title
        :caption-loc    :bulletin-report.description
        :button-loc     :authorityAdmin.authoritiesReport.buttonText
        :start-date-loc :bulletin-report.start-date
        :end-date-loc   :bulletin-report.end-date}]]
     [:section
      [simple-report :reviews-period-report
       {:title-loc   :authorityAdmin.applicationsReviewsTimeperiod.title
        :caption-loc :authorityAdmin.applicationsReviewsTimeperiod.description
        :button-loc  :authorityAdmin.applicationsReviewsTimeperiod.buttonText}]]]]])
